import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
// utils
import axiosInstance from '../utils/axios';

// ----------------------------------------------------------------------
// Which access-control system runs at the campus you are looking at.
//
// One provider is live per campus, decided server-side. An operator can run
// Spintly across the estate and Kisi at one acquired building, so the answer
// changes with the campus switcher, not just with the org. Components should
// branch on `capabilities`, not on `provider` — that way a provider that gains
// a feature later needs no change here or in the pages that use it.
//
//   const { provider, capabilities, loading } = useAccessProvider();
//   if (capabilities.cards) { ...show the card assignment tab... }
// ----------------------------------------------------------------------

const PROVIDER_ENDPOINT = '/api/v1/access-control/provider/';

const EMPTY_CAPABILITIES = {
  remote_unlock: false,
  member_unlock: false,
  ble_unlock: false,
  api_door_list: false,
  cards: false,
  qr: false,
  // Doors are opened by typing a code, so there is nothing to press: the doors
  // page renders the code instead of an Unlock button. Anything that gates the
  // doors UI on member_unlock alone must check this too.
  unlock_code: false,
  visitor_pass: false,
  native_time_windows: false,
  event_push: false,
};

const LOADING_STATE = {
  provider: null,
  label: null,
  capabilities: EMPTY_CAPABILITIES,
  configuredProviders: [],
  loading: true,
  error: null,
};

// The provider changes only when an admin re-points the integration, but the
// nav, the bottom nav and the page all ask on every render pass. Share one
// result across consumers so mounting the dashboard makes a single request
// instead of one per component.
//
// The cache is keyed by campus. Only one campus is active at a time, so a
// single slot is enough — but it has to remember which campus it describes,
// because the admin campus switcher does not reload the page. Without the key,
// switching from a Spintly building to a Kisi one would keep serving 'spintly'
// to every consumer, and `remoteUnlockUrl` would POST an unlock to the wrong
// vendor's API.
let cachedKey;
let cachedState = null;
let inFlight = null;
let inFlightKey;
const listeners = new Set();

function loadProvider(campusId, force) {
  const key = campusId || null;

  if (!force) {
    if (cachedState && cachedKey === key) return Promise.resolve(cachedState);
    if (inFlight && inFlightKey === key) return inFlight;
  }

  if (cachedKey !== key) {
    // Drop straight to loading rather than leave the previous building's
    // provider on screen: the setup link and the unlock URL are vendor
    // specific, so a stale answer is worse than no answer.
    cachedState = null;
    cachedKey = key;
    listeners.forEach((notify) => notify(LOADING_STATE));
  }

  inFlightKey = key;
  inFlight = axiosInstance
    // Sent explicitly even though the axios interceptor injects the active
    // campus: this request must describe the campus React just rendered for,
    // not whatever localStorage happens to hold when it goes out.
    .get(PROVIDER_ENDPOINT, key ? { params: { campus_id: key } } : undefined)
    .then(({ data }) => ({
      provider: data.provider || null,
      label: data.label || null,
      capabilities: { ...EMPTY_CAPABILITIES, ...(data.capabilities || {}) },
      configuredProviders: data.configured_providers || [],
      loading: false,
      error: null,
    }))
    .catch((err) => ({
      // Treat a failure as "no provider" rather than guessing. Showing an empty
      // access screen is recoverable; showing the wrong vendor's controls is not.
      provider: null,
      label: null,
      capabilities: EMPTY_CAPABILITIES,
      configuredProviders: [],
      loading: false,
      error: err?.response?.data?.error || 'Could not determine access provider',
    }))
    .then((next) => {
      // A campus switch while this was in flight wins: publishing now would
      // overwrite the newer campus's answer with the one the user left.
      if (inFlightKey !== key) return next;
      cachedState = next;
      cachedKey = key;
      inFlight = null;
      listeners.forEach((notify) => notify(next));
      return next;
    });

  return inFlight;
}

// Called on logout: the next user may belong to a different organisation, and
// this module outlives the React tree because logout does not reload the page.
export function clearAccessProviderCache() {
  cachedState = null;
  cachedKey = undefined;
  inFlight = null;
  inFlightKey = undefined;
  listeners.forEach((notify) => notify(LOADING_STATE));
}

// Called after something repoints the integration — the settings toggle. Unlike
// clearAccessProviderCache this re-reads rather than just invalidating, because
// consumers already mounted elsewhere (the phone bottom nav) have no effect to
// re-run and would otherwise sit at "loading" until they remounted.
export function refreshAccessProvider() {
  return loadProvider(cachedKey ?? null, true);
}

export default function useAccessProvider() {
  const campusId = useSelector((state) => state.meta.activeCampus?.id || null);
  const [state, setState] = useState(() =>
    cachedState && cachedKey === campusId ? cachedState : LOADING_STATE
  );

  useEffect(() => {
    let alive = true;
    const notify = (next) => {
      if (alive) setState(next);
    };
    listeners.add(notify);
    loadProvider(campusId, false).then(notify);

    return () => {
      alive = false;
      listeners.delete(notify);
    };
  }, [campusId]);

  const refresh = useCallback(() => loadProvider(campusId, true), [campusId]);

  return { ...state, refresh };
}

// Base path for the live provider's integration endpoints, so callers build
// URLs from one place instead of hardcoding a vendor.
export function providerApiBase(provider) {
  if (!provider) return null;
  // Some slugs differ from their URL segment: Django routes use hyphens.
  // Missing an entry here is silent — the slug passes through unchanged and
  // every call 404s with nothing to say the map was the problem.
  const slug = {
    deskos_access: 'deskos-access',
    code_unlock: 'code-unlock',
    spintly_saams: 'spintly-saams',
  }[provider] || provider;
  return `/api/v1/integrations/${slug}`;
}

export function remoteUnlockUrl(provider, capabilities) {
  const base = providerApiBase(provider);
  if (!base) return null;
  // Not every provider can be told to open a door from the server. Spintly
  // SAAMS unlocks over BLE from the member's phone and exposes no such
  // endpoint, so building a URL for it produced a 404 that read like a bug in
  // the integration. When capabilities are known, let them decide; when they
  // are not passed, keep the old behaviour so existing callers are unchanged.
  if (capabilities && !capabilities.remote_unlock) return null;
  // deskos-access names this endpoint differently to the other two.
  return provider === 'deskos_access'
    ? `${base}/door-unlock/`
    : `${base}/remote-unlock/`;
}

// True when this provider gives the member something usable on a doors screen —
// a button to press, or a code to read. Both the bottom nav and the doors page
// ask this, and they must not drift: gating on member_unlock alone hid the tab
// for Code Unlock, whose entire member-facing feature is that screen.
export function hasMemberDoorsUI(capabilities) {
  return Boolean(capabilities?.member_unlock || capabilities?.unlock_code);
}
